// **LAST-RESORT SAFETY NET — not the primary resolution path.** Most modems
// will tell you the operator's name directly if you ask them right. Before
// reaching for this table, issue `AT+COPS=3,0` (set read format to
// alpha-long) and then `AT+COPS?` — the modem returns "Hot Mobile" instead
// of "42507". That's the canonical fix, implemented in
// `apps/frontend-new/functions/src/flows/executors/PrimitiveCallExecutor.ts`
// (`queryLiveCellularState`). This table only kicks in when:
//
//   - A vendor firmware ignores `AT+COPS=3,0` and keeps returning numeric
//     (rare; we haven't actually observed it on Quectel BG95/EG25/RM520N
//     in production, but kept as defence-in-depth).
//   - We never got a chance to issue `=3,0` first because the only data
//     we have is a numeric PLMN from a non-COPS source (`AT+QNWINFO`,
//     `AT+QENG`).
//
// Resolution priority used by `enrichParentExecution`:
//
//   1. `AT+COPS=3,0; AT+COPS?` on-demand — modem-native alpha-long name.
//      This is the canonical answer.
//   2. Modem's `available_networks[]` scan cache — matches what the modem
//      just saw on the air; some networks present here may not be in any
//      central catalog at all.
//   3. Firestore `networks/` collection — curated by ops, per-deployment
//      authoritative source. Updated as new roaming markets onboard.
//   4. **This static table** — last-resort safety net only. The curated
//      `PLMN_TABLE` overrides below take precedence; anything not covered
//      there falls through to `MCC_MNC_OPERATORS` — the full ~2k-entry map
//      auto-generated from the public MCC/MNC allocation list
//      (scripts/generate-mcc-mnc-data.mjs). So unseen roaming PLMNs now
//      resolve to a real operator name instead of a bare number.
//   5. (no match) — caller renders just the PLMN.
//
// Coverage focuses on test markets actively in use: IL (425, production
// edge in Tel Aviv), US (310/311/312/313/316, NY edge), plus tier-1 EU
// (DE/FR/UK/NL/ES/IT/CH) + APAC (AU/JP/SG) carriers seen in roaming.
// When a new roaming market gets exercised, add the operators to the
// Firestore `networks/` collection FIRST — that's the curated authoritative
// source. Only add entries here if a specific modem has been observed
// returning numeric PLMN despite the `AT+COPS=3,0` request.
//
// Also exported and used by:
//   - apps/frontend-new/src/services/phoneWebSocketService.ts (TLAM status
//     strip) — calls formatOperatorFromMccmnc() to produce "Cellcom IL
//     (42502)" style labels.
//
// History: originally at apps/frontend-new/src/utils/plmnTable.ts (commit
// a2a91ca, 2026-04-26) where it shipped as a shotgun fix against any
// numeric-PLMN case regardless of root cause. Moved to shared-types on
// 2026-05-20 when the backend needed access too, then immediately demoted
// the same day to "safety net only" after the realization that
// `AT+COPS=3,0` plus `AT+COPS?` is the canonical fix (the modem already
// has the operator name; we just have to ask in the right format). See
// docs/sessions/SESSION_2026_05_20_EDGE_AS_LAST_RESORT_AND_OPERATOR_NAME_FIX.md
import { MCC_MNC_OPERATORS } from './mccMncOperators';
// Curated overrides. These names carry deployment-specific context (e.g.
// "Cellcom IL" / "Vodafone UK") that the raw allocation list doesn't, and
// take precedence over the auto-generated `MCC_MNC_OPERATORS` fallback.
const PLMN_TABLE = {
    // Israel (425)
    '42501': 'Partner IL',
    '42502': 'Cellcom IL',
    '42503': 'Pelephone',
    '42505': 'Jawwal',
    '42506': 'Wataniya',
    '42507': 'Hot Mobile',
    '42508': 'Golan Telecom',
    '42509': '019 Mobile',
    '42511': 'Hot Mobile',
    '42512': 'Cellcom IL',
    '42514': 'Pelephone',
    '42515': 'Free Telecom',
    '42516': 'Rami Levy',
    '42517': 'Annatel',
    '42519': 'Home Cellular',
    // USA (310, 311, 312, 313, 316)
    '310030': 'AT&T',
    '310070': 'AT&T',
    '310080': 'AT&T',
    '310150': 'AT&T',
    '310170': 'AT&T',
    '310280': 'AT&T',
    '310380': 'AT&T',
    '310410': 'AT&T',
    '310560': 'AT&T',
    '310680': 'AT&T',
    '310090': 'Verizon',
    '310590': 'Verizon',
    '311270': 'Verizon',
    '311480': 'Verizon',
    '310160': 'T-Mobile',
    '310200': 'T-Mobile',
    '310210': 'T-Mobile',
    '310220': 'T-Mobile',
    '310230': 'T-Mobile',
    '310240': 'T-Mobile',
    '310250': 'T-Mobile',
    '310260': 'T-Mobile',
    '310270': 'T-Mobile',
    '310300': 'T-Mobile',
    '310310': 'T-Mobile',
    '310490': 'T-Mobile',
    '310660': 'T-Mobile',
    '310800': 'T-Mobile',
    '311882': 'T-Mobile',
    '312530': 'Sprint (T-Mobile)',
    '316010': 'Southern Linc',
    // Canada (302)
    '302220': 'Telus',
    '302610': 'Bell',
    '302720': 'Rogers',
    '302780': 'SaskTel',
    // United Kingdom (234, 235)
    '23410': 'O2',
    '23415': 'Vodafone UK',
    '23420': '3 UK',
    '23430': 'EE',
    '23433': 'EE',
    '23434': 'EE',
    // Germany (262)
    '26201': 'Telekom DE',
    '26202': 'Vodafone DE',
    '26203': 'O2 DE',
    '26207': 'O2 DE',
    // France (208)
    '20801': 'Orange',
    '20810': 'SFR',
    '20815': 'Free Mobile',
    '20820': 'Bouygues',
    // Netherlands (204)
    '20402': 'Tele2 NL',
    '20404': 'Vodafone NL',
    '20408': 'KPN',
    '20416': 'T-Mobile NL',
    // Spain (214)
    '21401': 'Vodafone ES',
    '21403': 'Orange ES',
    '21404': 'Yoigo',
    '21407': 'Movistar',
    // Italy (222)
    '22201': 'TIM',
    '22210': 'Vodafone IT',
    '22288': 'Wind Tre',
    '22299': 'Iliad IT',
    // Switzerland (228)
    '22801': 'Swisscom',
    '22802': 'Sunrise',
    '22803': 'Salt',
    // Australia (505)
    '50501': 'Telstra',
    '50502': 'Optus',
    '50503': 'Vodafone AU',
    // Japan (440, 441)
    '44010': 'NTT Docomo',
    '44020': 'SoftBank',
    '44050': 'KDDI au',
    '44051': 'KDDI au',
    '44053': 'KDDI au',
    // Singapore (525)
    '52501': 'SingTel',
    '52502': 'SingTel',
    '52503': 'M1',
    '52505': 'StarHub',
};
/**
 * Returns true if the string looks like a 5- or 6-digit PLMN (MCC+MNC).
 */
export function looksLikeNumericPlmn(s) {
    return /^\d{5,6}$/.test(s);
}
/**
 * Resolve a numeric MCCMNC to a friendly operator label.
 *
 * Returns one of:
 *  - `"Cellcom IL (42502)"` when a match is found
 *  - the original `mccmnc` string when no match — caller decides how to render
 */
export function formatOperatorFromMccmnc(mccmnc) {
    const name = lookupOperatorName(mccmnc);
    return name ? `${name} (${mccmnc})` : mccmnc;
}
/**
 * Resolve a numeric MCCMNC to JUST the operator name (no MCCMNC suffix).
 * Returns `null` when the table has no entry — caller falls back to other
 * sources (Firestore `networks/` collection, modem scan cache, etc.).
 */
export function lookupOperatorName(mccmnc) {
    return PLMN_TABLE[mccmnc] ?? MCC_MNC_OPERATORS[mccmnc] ?? null;
}
